import React, { useMemo, useState } from 'react';
import { Box, Button, Grid, IconButton, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { BoxDetailsType, BoxType, GetRatesContextType } from '../../Type';
import GetRatesContext from '../../../../context/GetRates';
import { calculateVolume } from '../../../../services/calculateVolume';
import BoxDelete from '../BoxDelete/BoxDelete';
import { LENGTH_UNITS, defaultBox } from '../../Constant';
import updateBoxId from '@Services/updateBoxId';

function BoxDetails({ setBoxState, data }: BoxDetailsType) {
  const theme = useTheme();
  const { boxes, setBoxes, ratesInput } = React.useContext(GetRatesContext) as GetRatesContextType;
  const [openDeleteBox, setOpenDeleteBox] = useState({ open: false });
  const { id = '0', length, height, width, weight } = data;
  const { weightUnit } = ratesInput;
  const dimUnit = LENGTH_UNITS[weightUnit]?.value || 'cm';

  const volumetricWeight = useMemo(
    () => calculateVolume(length, width, height, dimUnit as any),
    [length, width, height, dimUnit],
  );

  function deleteConfirm() {
    let boxesTemp = [...boxes];
    if (id) {
      boxesTemp.splice(parseInt(id), 1);
      boxesTemp = boxesTemp.map((box, ind) => ({ ...box, id: ind.toString() }));
    }
    const arr = updateBoxId<BoxType>(boxesTemp, defaultBox, 0);
    setBoxes([...arr]);
    handleCloseModal();
  }

  function onDelete() {
    if (boxes.length > 1) setOpenDeleteBox({ open: true });
  }

  const handleCloseModal = () => {
    setOpenDeleteBox({ open: false });
  };

  const actualWeight = typeof weight === 'string' ? parseFloat(weight || '0') : weight || 0;
  const chargeableWeight = volumetricWeight > actualWeight ? volumetricWeight : actualWeight;

  const hasDims = length && width && height && Number(length) > 0 && Number(width) > 0 && Number(height) > 0;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Card Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: '0.8rem',
              px: 1,
              py: 0.35,
              borderRadius: '6px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Box {parseInt(id) + 1} of {boxes.length}
          </Box>
          {hasDims && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}>
              {length} × {width} × {height} {dimUnit}
            </Typography>
          )}
        </Box>

        <Tooltip title={boxes.length <= 1 ? 'Minimum 1 box required' : 'Delete this box'}>
          <span>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={boxes.length <= 1}
              sx={{
                color: boxes.length <= 1 ? 'text.disabled' : theme.palette.error.main,
                p: 0.5,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Weight Summary Metrics */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          p: 1.25,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Actual Weight
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}>
            {actualWeight || 0} {weightUnit}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Volumetric Weight
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem' }}>
            {volumetricWeight || 0} {weightUnit}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 0.5,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.78rem' }}>
            Chargeable Weight
          </Typography>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              color: theme.palette.primary.main,
              px: 0.75,
              py: 0.2,
              borderRadius: '4px',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            {chargeableWeight} {weightUnit}
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Grid container spacing={1} mt={0.25}>
        <Grid item xs={6}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => setBoxState('edit')}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 0.6,
              color: 'text.primary',
              borderColor: 'divider',
              bgcolor: '#fff',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Edit
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => setBoxState('copy')}
            startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 0.6,
              color: 'text.primary',
              borderColor: 'divider',
              bgcolor: '#fff',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Duplicate
          </Button>
        </Grid>
      </Grid>

      <BoxDelete open={openDeleteBox.open} onClose={handleCloseModal} onConfirm={deleteConfirm} />
    </Box>
  );
}

export default BoxDetails;
